/* Created by Interactive 63 - https://interactive63.com */
/**
 * An editable form of path data: subpaths of points, each with optional curve handles. Straight
 * segments stay straight lines until a handle is pulled out of them, so editing a simple shape
 * does not fill its data with curves. Arcs and quadratic curves become cubic curves on reading.
 */
import svgpath from 'svgpath';
import type { Vec2 } from '../model/types';
import { mirroredHandle } from '../timeline/motion-curve';

export interface EditNode {
  x: number;
  y: number;
  /** Handle shaping the segment arriving at this point, as an offset from it. */
  in?: Vec2;
  /** Handle shaping the segment leaving this point, as an offset from it. */
  out?: Vec2;
  /** The two handles stay in a straight line through the point. */
  smooth: boolean;
}

export interface EditSubpath {
  nodes: EditNode[];
  closed: boolean;
}

export interface EditablePath {
  subpaths: EditSubpath[];
}

/** A point, by subpath and position within it. */
export interface NodeRef {
  subpath: number;
  node: number;
}

/** The segment leaving a point; on a closed subpath the last point's segment returns to the first. */
export type SegmentRef = NodeRef;

/** Decimal places written back to path data. */
const DEFAULT_DECIMALS = 3;
/** Points sampled along a segment to find the spot nearest the pointer. */
const NEAREST_SAMPLES = 64;
/** How far towards each neighbour new smooth handles reach. */
const SMOOTH_HANDLE_SHARE = 1 / 3;
const EPSILON = 1e-9;

/** A handle with any length, or undefined. */
function handle(dx: number, dy: number): Vec2 | undefined {
  return Math.hypot(dx, dy) > EPSILON ? { x: dx, y: dy } : undefined;
}

/** Whether two handles point in opposite directions along one line. */
function inLine(a: Vec2 | undefined, b: Vec2 | undefined): boolean {
  if (!a || !b) return false;
  const cross = a.x * b.y - a.y * b.x;
  const scale = Math.hypot(a.x, a.y) * Math.hypot(b.x, b.y);
  return Math.abs(cross) <= 1e-3 * scale && a.x * b.x + a.y * b.y < 0;
}

/**
 * Reads path data into editable points. Returns null when the data cannot be read or draws
 * nothing.
 */
export function parseEditable(d: string): EditablePath | null {
  const subpaths: EditSubpath[] = [];
  let current: EditSubpath | null = null;
  let start = { x: 0, y: 0 };
  /** The subpath being drawn, starting a new one after a close without a move. */
  const open = (x: number, y: number): EditSubpath => {
    if (!current || current.closed) {
      current = { nodes: [{ x, y, smooth: false }], closed: false };
      subpaths.push(current);
    }
    return current;
  };
  // Set from inside the iteration callback when a command cannot be read.
  const status = { failed: false };
  try {
    svgpath(d)
      .abs()
      .unshort()
      .unarc()
      .iterate((seg, _index, x, y) => {
        const command = seg[0];
        if (command === 'M') {
          current = { nodes: [{ x: seg[1], y: seg[2], smooth: false }], closed: false };
          subpaths.push(current);
          start = { x: seg[1], y: seg[2] };
          return;
        }
        if (command === 'Z' || command === 'z') {
          const sub = open(start.x, start.y);
          sub.closed = true;
          const first = sub.nodes[0];
          const last = sub.nodes[sub.nodes.length - 1];
          // A drawn segment back to the start duplicates the first point; fold it in.
          if (
            first &&
            last &&
            sub.nodes.length > 1 &&
            Math.hypot(last.x - first.x, last.y - first.y) < 1e-6
          ) {
            if (last.in) first.in = last.in;
            sub.nodes.pop();
          }
          return;
        }
        const sub = open(x, y);
        const prev = sub.nodes[sub.nodes.length - 1] as EditNode;
        switch (command) {
          case 'L':
            sub.nodes.push({ x: seg[1], y: seg[2], smooth: false });
            break;
          case 'H':
            sub.nodes.push({ x: seg[1], y, smooth: false });
            break;
          case 'V':
            sub.nodes.push({ x, y: seg[1], smooth: false });
            break;
          case 'C': {
            const out = handle(seg[1] - prev.x, seg[2] - prev.y);
            if (out) prev.out = out;
            const into = handle(seg[3] - seg[5], seg[4] - seg[6]);
            sub.nodes.push({ x: seg[5], y: seg[6], ...(into ? { in: into } : {}), smooth: false });
            break;
          }
          case 'Q': {
            // A quadratic curve as the cubic that draws it exactly.
            const [qx, qy, ex, ey] = [seg[1], seg[2], seg[3], seg[4]];
            const out = handle(((qx - prev.x) * 2) / 3, ((qy - prev.y) * 2) / 3);
            if (out) prev.out = out;
            const into = handle(((qx - ex) * 2) / 3, ((qy - ey) * 2) / 3);
            sub.nodes.push({ x: ex, y: ey, ...(into ? { in: into } : {}), smooth: false });
            break;
          }
          default:
            status.failed = true;
        }
      });
  } catch {
    return null;
  }
  if (status.failed || subpaths.length === 0) return null;
  for (const sub of subpaths) for (const node of sub.nodes) node.smooth = inLine(node.in, node.out);
  return { subpaths };
}

/** Writes a number with at most the given decimals and no trailing zeros. */
function num(value: number, decimals: number): string {
  const factor = 10 ** decimals;
  return String(Math.round(value * factor) / factor + 0);
}

/** Writes editable points back as compact path data. */
export function formatEditable(path: EditablePath, decimals = DEFAULT_DECIMALS): string {
  const n = (value: number): string => num(value, decimals);
  /** One segment from a point to the next: a line unless either end has a handle. */
  const segment = (from: EditNode, to: EditNode): string => {
    if (!from.out && !to.in) return `L${n(to.x)} ${n(to.y)}`;
    const c1 = { x: from.x + (from.out?.x ?? 0), y: from.y + (from.out?.y ?? 0) };
    const c2 = { x: to.x + (to.in?.x ?? 0), y: to.y + (to.in?.y ?? 0) };
    return `C${n(c1.x)} ${n(c1.y)} ${n(c2.x)} ${n(c2.y)} ${n(to.x)} ${n(to.y)}`;
  };
  const parts: string[] = [];
  for (const sub of path.subpaths) {
    const first = sub.nodes[0];
    if (!first) continue;
    parts.push(`M${n(first.x)} ${n(first.y)}`);
    sub.nodes.forEach((node, i) => {
      const prev = sub.nodes[i - 1];
      if (prev) parts.push(segment(prev, node));
    });
    const last = sub.nodes[sub.nodes.length - 1];
    if (sub.closed) {
      if (last && sub.nodes.length > 1 && (last.out || first.in)) parts.push(segment(last, first));
      parts.push('Z');
    }
  }
  return parts.join('');
}

/** A deep copy, so edits never change the path they started from. */
function clone(path: EditablePath): EditablePath {
  return {
    subpaths: path.subpaths.map((sub) => ({
      closed: sub.closed,
      nodes: sub.nodes.map((node) => ({
        ...node,
        ...(node.in ? { in: { ...node.in } } : {}),
        ...(node.out ? { out: { ...node.out } } : {}),
      })),
    })),
  };
}

/** The point a reference names. */
export function nodeAt(path: EditablePath, ref: NodeRef): EditNode | undefined {
  return path.subpaths[ref.subpath]?.nodes[ref.node];
}

/** Moves points, with their handles, by an offset. */
export function moveNodes(
  path: EditablePath,
  refs: readonly NodeRef[],
  dx: number,
  dy: number,
): EditablePath {
  const next = clone(path);
  for (const ref of refs) {
    const node = nodeAt(next, ref);
    if (!node) continue;
    node.x += dx;
    node.y += dy;
  }
  return next;
}

/**
 * Sets one handle of a point. On a smooth point the other handle turns to stay in line, keeping
 * its length, unless `breakCorner` makes the point a corner.
 */
export function setHandle(
  path: EditablePath,
  ref: NodeRef,
  side: 'in' | 'out',
  value: Vec2,
  breakCorner: boolean,
): EditablePath {
  const next = clone(path);
  const node = nodeAt(next, ref);
  if (!node) return next;
  node[side] = value;
  if (breakCorner) node.smooth = false;
  if (node.smooth) {
    const other = side === 'in' ? 'out' : 'in';
    const mirrored = mirroredHandle(value, node[other]);
    if (mirrored) node[other] = mirrored;
  }
  return next;
}

/** The two neighbours of a point, wrapping round a closed subpath. */
function neighbours(sub: EditSubpath, index: number): [EditNode | undefined, EditNode | undefined] {
  const count = sub.nodes.length;
  const wrap = (i: number): EditNode | undefined =>
    sub.closed ? sub.nodes[(i + count) % count] : sub.nodes[i];
  return [count > 1 ? wrap(index - 1) : undefined, count > 1 ? wrap(index + 1) : undefined];
}

/** Gives a straight point smooth handles, or makes a curved point a straight corner. */
export function toggleSmooth(path: EditablePath, ref: NodeRef): EditablePath {
  const next = clone(path);
  const sub = next.subpaths[ref.subpath];
  const node = sub?.nodes[ref.node];
  if (!sub || !node) return next;
  if (node.in || node.out) {
    delete node.in;
    delete node.out;
    node.smooth = false;
    return next;
  }
  const [prev, after] = neighbours(sub, ref.node);
  const through = {
    x: (after ?? node).x - (prev ?? node).x,
    y: (after ?? node).y - (prev ?? node).y,
  };
  const size = Math.hypot(through.x, through.y);
  if (size < EPSILON) return next;
  const unit = { x: through.x / size, y: through.y / size };
  const reach = (other: EditNode | undefined): number =>
    other ? Math.hypot(other.x - node.x, other.y - node.y) * SMOOTH_HANDLE_SHARE : 0;
  if (prev) node.in = { x: -unit.x * reach(prev), y: -unit.y * reach(prev) };
  if (after) node.out = { x: unit.x * reach(after), y: unit.y * reach(after) };
  node.smooth = true;
  return next;
}

/** Removes points. Refused, with a reason, when a part would be left with fewer than two. */
export function deleteNodes(
  path: EditablePath,
  refs: readonly NodeRef[],
): { path: EditablePath } | { reason: string } {
  const next = clone(path);
  for (const [s, sub] of next.subpaths.entries()) {
    const doomed = new Set(refs.filter((r) => r.subpath === s).map((r) => r.node));
    if (doomed.size === 0) continue;
    if (sub.nodes.length - doomed.size < 2) {
      return { reason: 'Each part of a path needs at least two points.' };
    }
    sub.nodes = sub.nodes.filter((_, i) => !doomed.has(i));
  }
  return { path: next };
}

/** The four control points of a segment. */
function controls(path: EditablePath, seg: SegmentRef): [Vec2, Vec2, Vec2, Vec2] | null {
  const sub = path.subpaths[seg.subpath];
  const from = sub?.nodes[seg.node];
  const to = sub
    ? seg.node + 1 < sub.nodes.length
      ? sub.nodes[seg.node + 1]
      : sub.closed
        ? sub.nodes[0]
        : undefined
    : undefined;
  if (!from || !to) return null;
  return [
    { x: from.x, y: from.y },
    { x: from.x + (from.out?.x ?? 0), y: from.y + (from.out?.y ?? 0) },
    { x: to.x + (to.in?.x ?? 0), y: to.y + (to.in?.y ?? 0) },
    { x: to.x, y: to.y },
  ];
}

/** Linear interpolation between two points. */
function lerp(a: Vec2, b: Vec2, t: number): Vec2 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/** The point on a segment at parameter t. */
export function segmentPoint(path: EditablePath, seg: SegmentRef, t: number): Vec2 | null {
  const c = controls(path, seg);
  if (!c) return null;
  const ab = lerp(c[0], c[1], t);
  const bc = lerp(c[1], c[2], t);
  const cd = lerp(c[2], c[3], t);
  return lerp(lerp(ab, bc, t), lerp(bc, cd, t), t);
}

/** Every segment of a path, for drawing and hit testing. */
export function segmentsOf(path: EditablePath): SegmentRef[] {
  return path.subpaths.flatMap((sub, s) =>
    sub.nodes.flatMap((_, i) =>
      i + 1 < sub.nodes.length || (sub.closed && sub.nodes.length > 1)
        ? [{ subpath: s, node: i }]
        : [],
    ),
  );
}

/** The parameter on a segment nearest to a point. */
export function nearestOnSegment(path: EditablePath, seg: SegmentRef, point: Vec2): number {
  let best = 0;
  let bestDistance = Infinity;
  for (let i = 0; i <= NEAREST_SAMPLES; i += 1) {
    const t = i / NEAREST_SAMPLES;
    const p = segmentPoint(path, seg, t);
    if (!p) continue;
    const distance = Math.hypot(p.x - point.x, p.y - point.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = t;
    }
  }
  return best;
}

/**
 * Adds a point on a segment at parameter t without changing the shape: a curve is split in two
 * curves, a straight segment in two lines. Returns the new path and the new point's reference.
 */
export function insertNode(
  path: EditablePath,
  seg: SegmentRef,
  t: number,
): { path: EditablePath; ref: NodeRef } | null {
  const c = controls(path, seg);
  const next = clone(path);
  const sub = next.subpaths[seg.subpath];
  if (!c || !sub) return null;
  const fromIndex = seg.node;
  const toIndex = (seg.node + 1) % sub.nodes.length;
  const from = sub.nodes[fromIndex] as EditNode;
  const to = sub.nodes[toIndex] as EditNode;
  const curved = from.out !== undefined || to.in !== undefined;
  const ab = lerp(c[0], c[1], t);
  const bc = lerp(c[1], c[2], t);
  const cd = lerp(c[2], c[3], t);
  const abc = lerp(ab, bc, t);
  const bcd = lerp(bc, cd, t);
  const point = lerp(abc, bcd, t);
  const node: EditNode = { x: point.x, y: point.y, smooth: curved };
  if (curved) {
    from.out = handle(ab.x - from.x, ab.y - from.y);
    if (!from.out) delete from.out;
    to.in = handle(cd.x - to.x, cd.y - to.y);
    if (!to.in) delete to.in;
    const into = handle(abc.x - point.x, abc.y - point.y);
    const out = handle(bcd.x - point.x, bcd.y - point.y);
    if (into) node.in = into;
    if (out) node.out = out;
  }
  sub.nodes.splice(fromIndex + 1, 0, node);
  return { path: next, ref: { subpath: seg.subpath, node: fromIndex + 1 } };
}
